import React, { useState, ChangeEvent } from 'react';
import {
  Box, Button, Typography, TextField, Avatar,
  ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import BarcodeScanner from './BarcodeScanner';
import { useLanguage } from '../i18n/LanguageContext';

interface FoodProduct {
  name: string;
  brand: string;
  kcal_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fat_per_100g: number;
  image_url: string;
  serving_size: string;
  barcode: string;
}

export interface ScannedItem {
  product: FoodProduct;
  amount: number;
  unit: 'g' | 'ml' | 'cl';
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

interface Props {
  onDone: (items: ScannedItem[]) => void;
  onCancel: () => void;
}

type Unit = 'g' | 'ml' | 'cl';
const unitMultiplier: Record<Unit, number> = { g: 1, ml: 1, cl: 10 };

function isLiquid(product: FoodProduct): boolean {
  return /\b(m?l|cl|dl)\b/i.test(product.serving_size ?? '');
}

function computeNutrition(product: FoodProduct, amount: number, unit: Unit) {
  const factor = (amount * unitMultiplier[unit]) / 100;
  return {
    calories: Math.round(factor * product.kcal_per_100g),
    protein_g: Math.round(factor * product.protein_per_100g * 10) / 10,
    carbs_g: Math.round(factor * product.carbs_per_100g * 10) / 10,
    fat_g: Math.round(factor * product.fat_per_100g * 10) / 10,
  };
}

const BarcodeMealScanner: React.FC<Props> = ({ onDone, onCancel }) => {
  const { t } = useLanguage();
  const [view, setView] = useState<'scanning' | 'confirming'>('scanning');
  const [pending, setPending] = useState<FoodProduct | null>(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<Unit>('g');
  const [items, setItems] = useState<ScannedItem[]>([]);

  const handleProductScanned = (product: FoodProduct) => {
    setPending(product);
    setAmount('');
    setUnit(isLiquid(product) ? 'ml' : 'g');
    setView('confirming');
  };

  const buildItem = (): ScannedItem | null => {
    if (!pending) return null;
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) return null;
    const nutrition = computeNutrition(pending, amountNum, unit);
    return { product: pending, amount: amountNum, unit, ...nutrition };
  };

  const handleAddAndScanNext = () => {
    const item = buildItem();
    if (item) setItems(prev => [...prev, item]);
    setPending(null);
    setView('scanning');
  };

  const handleAddAndDone = () => {
    const item = buildItem();
    const finalItems = item ? [...items, item] : items;
    if (finalItems.length > 0) onDone(finalItems);
  };

  const handleDeleteItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const parsedAmount = parseFloat(amount);
  const previewCalories =
    pending && parsedAmount > 0
      ? computeNutrition(pending, parsedAmount, unit).calories
      : null;

  if (view === 'scanning') {
    return (
      <Box>
        <BarcodeScanner onProductScanned={handleProductScanned} onClose={onCancel} />
        {items.length > 0 && (
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => onDone(items)}
          >
            {t.doneScanItems.replace('{n}', String(items.length))}
          </Button>
        )}
      </Box>
    );
  }

  // confirming view
  return (
    <Box>
      {/* Product card */}
      {pending && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Avatar
            src={pending.image_url}
            alt={pending.name}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              {pending.name}
            </Typography>
            {pending.brand && (
              <Typography variant="body2" color="text.secondary">
                {pending.brand}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {pending.kcal_per_100g} kcal / 100{unit === 'g' ? 'g' : unit}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Amount row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          label={t.amount}
          type="number"
          size="small"
          value={amount}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ width: 110 }}
        />
        <ToggleButtonGroup
          value={unit}
          exclusive
          size="small"
          onChange={(_e, val) => { if (val) setUnit(val as Unit); }}
        >
          <ToggleButton value="g">g</ToggleButton>
          <ToggleButton value="ml">ml</ToggleButton>
          <ToggleButton value="cl">cl</ToggleButton>
        </ToggleButtonGroup>
        {previewCalories !== null && (
          <Typography variant="body2" color="text.secondary">
            ≈ {previewCalories} kcal
          </Typography>
        )}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAddAndScanNext}
          disabled={!parsedAmount || parsedAmount <= 0}
        >
          {t.addAndScanNext}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleAddAndDone}
          disabled={!parsedAmount || parsedAmount <= 0}
        >
          {t.addAndDone}
        </Button>
      </Box>

      {/* Accumulated items */}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((item, idx) => (
            <Chip
              key={idx}
              label={`${item.product.name} · ${item.amount}${item.unit} · ${item.calories} kcal`}
              size="small"
              onDelete={() => handleDeleteItem(idx)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BarcodeMealScanner;
